import { createAsyncThunk } from '@reduxjs/toolkit'
import OrderServiceProvider from 'src/services/OrderServiceProvider'
import UserDataServiceProvider from 'src/services/UserDataServiceProvider'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { setAddresses, setAvailableTransports } from './cartStore'
import {
  checkPriceChangesForCartProducts,
  getPricesandAvailability,
  hashTransport,
} from './helpers'
import { RootState, store } from 'store'
import { flattenCartErrors, hasSameErrors } from '../Utils'
import { SellerCartVehicle } from './ISellerCart'
import { findCartVehicle } from './cartSelectors'
import { setProcessingOrder } from './uiStore'

// Fetch transports
export const fetchTransportsThunk = createAsyncThunk(
  'cart/fetchTransports',
  async (_, { dispatch }) => {
    const transports = await OrderServiceProvider.getTransports()

    const processed = (transports || []).map((locationTransport) => ({
      ...locationTransport,
      transports: locationTransport.transports?.map((t) => {
        const id = hashTransport(t) // define your hashTransport helper
        return { ...t, id }
      }),
    }))

    dispatch(setAvailableTransports(processed))
  }
)

// Fetch ship-to addresses
export const fetchShipToAddressesThunk = createAsyncThunk(
  'cart/fetchAddresses',
  async (_, { dispatch }) => {
    const orgId = StoreInstances.userStore?.preferences.orgId
    const resp = await UserDataServiceProvider.getShipToAddresses(orgId)

    const addresses = (resp || []).map((address, index) => ({
      id: index.toString(),
      ...address,
    }))

    dispatch(setAddresses(addresses))
  }
)

export const validateCartPricesAndQty = createAsyncThunk<
  {
    hasSameCartErrors: boolean
    vehiclePriceChangeErrors: SellerCartVehicle[]
    hasTimeout: boolean
  },
  void,
  { state: RootState }
>('cart/validateCartPricesAndQty', async (_, { getState }) => {
  const stateSlice = getState().cart
  const vehicles = stateSlice.cartData.vehicles
  const previousErrors = flattenCartErrors(vehicles)

  const { hasTimeout } = await getPricesandAvailability(stateSlice).finally(
    () => store.dispatch(setProcessingOrder(false))
  )

  const updatedcartSlice = getState().cart
  const updatedCartVehicles = updatedcartSlice.cartData.vehicles

  const updatedErrors = flattenCartErrors(updatedCartVehicles)

  return {
    hasSameCartErrors: hasSameErrors(previousErrors, updatedErrors),
    vehiclePriceChangeErrors:
      checkPriceChangesForCartProducts(updatedCartVehicles),
    hasTimeout,
  }
})

export const validateSingleCartPricesAndQty = createAsyncThunk<
  {
    hasSameCartErrors: boolean
    vehiclePriceChangeErrors: SellerCartVehicle[]
    hasTimeout: boolean
  },
  SellerCartVehicle,
  { state: RootState }
>(
  'cart/validateSingleCartPricesAndQty',
  async (sellerCartVehicle, { getState }) => {
    const stateSlice = getState().cart
    const vehicle = findCartVehicle(getState(), sellerCartVehicle.vehicle)
    if (!vehicle)
      return {
        hasSameCartErrors: false,
        vehiclePriceChangeErrors: [],
        hasTimeout: false,
      }

    const previousErrors = flattenCartErrors([vehicle])

    const { hasTimeout } = await getPricesandAvailability(
      stateSlice,
      vehicle
    ).finally(() => store.dispatch(setProcessingOrder(false)))

    const updatedCartVehicle = findCartVehicle(
      getState(),
      sellerCartVehicle.vehicle
    )
    if (!updatedCartVehicle)
      return {
        hasSameCartErrors: false,
        vehiclePriceChangeErrors: [],
        hasTimeout: false,
      }

    const updatedErrors = flattenCartErrors([updatedCartVehicle])

    return {
      hasSameCartErrors: hasSameErrors(previousErrors, updatedErrors),
      vehiclePriceChangeErrors: checkPriceChangesForCartProducts([
        updatedCartVehicle,
      ]),
      hasTimeout,
    }
  }
)
